import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import api from './api';
import { Notification } from '../models/models';
import type { RootState } from './Store';

/** Toast actif affiché par <NotificationToastStack /> jusqu'à fermeture explicite par le user. */
export interface ActiveToast {
  notification_id: number;
  titre:           string;
  message:         string;
  niveau:          'INFO' | 'SUCCESS' | 'WARNING' | 'ERROR';
  lien:            string | null;
  arrived_at:      number;          // Date.now() — pour l'auto-purge anti-encombrement
}

const TOAST_STACK_MAX = 5;          // Limite du stack visible — FIFO au-delà

export interface SendBroadcastPayload {
  // ⚠ NE PAS renommer en `user_id` — l'intercepteur axios écraserait avec le user authentifié.
  target_user_id?: number | null;   // null = broadcast à tout le tenant
  titre:           string;
  message:         string;
  niveau?:         'INFO' | 'SUCCESS' | 'WARNING' | 'ERROR';
  popup?:          boolean;
  lien?:           string;
  expires_at?:     string;
}

interface FetchParams {
  only_unread?: boolean;
  limit?:       number;
  offset?:      number;
}

interface FetchResult {
  data:        Notification[];
  total:       number;
  totalPages:  number;
  currentPage: number;
}

interface NotificationState {
  notifications:  Notification[];
  total:          number;
  totalPages:     number;
  currentPage:    number;
  unreadCount:    number;
  loading:        boolean;
  sending:        boolean;
  error:          string | null;
  successMessage: string | null;
  /** Toasts persistants en cours d'affichage (popup=true uniquement) */
  activeToasts:   ActiveToast[];
}

const initialState: NotificationState = {
  notifications:  [],
  total:          0,
  totalPages:     0,
  currentPage:    0,
  unreadCount:    0,
  loading:        false,
  sending:        false,
  error:          null,
  successMessage: null,
  activeToasts:   [],
};

// ── Thunks ────────────────────────────────────────────────────

export const fetchNotifications = createAsyncThunk<
  FetchResult,
  FetchParams,
  { rejectValue: string }
>('notification/fetch', async (params, thunkAPI) => {
  try {
    const res = await api.get('/notifications', {
      params: {
        only_unread: params.only_unread ? 1 : 0,
        limit:       params.limit  ?? 20,
        offset:      params.offset ?? 0,
      },
    });
    return res.data;
  } catch (error: any) {
    return thunkAPI.rejectWithValue(
      error.response?.data?.error || error.response?.data?.message || 'Échec du chargement des notifications.',
    );
  }
});

export const fetchUnreadCount = createAsyncThunk<
  number,
  void,
  { rejectValue: string }
>('notification/fetchUnreadCount', async (_, thunkAPI) => {
  try {
    const res = await api.get('/notifications/unread/count');
    return res.data.total ?? 0;
  } catch (error: any) {
    return thunkAPI.rejectWithValue(
      error.response?.data?.error || error.response?.data?.message || 'Échec du compteur.',
    );
  }
});

/**
 * markAsRead — optimistic UI : la mise à jour state se fait dans `.pending`
 * (avant l'API), rollback dans `.rejected` en cas d'échec.
 *
 * Dedup via `condition` (s'exécute AVANT .pending) : si la notif est déjà lue
 * dans le state local, le thunk ne tourne pas du tout (ni pending, ni API,
 * ni fulfilled). Évite les double-POST.
 *
 * ⚠ NE PAS faire le check dans le payloadCreator : `.pending` aurait déjà
 *    mis lu=true, le check verrait toujours vrai → API jamais appelée → bug.
 */
export const markAsRead = createAsyncThunk<
  number,           // returns notification id
  number,           // notification id
  { state: RootState; rejectValue: string }
>(
  'notification/markAsRead',
  async (id, thunkAPI) => {
    try {
      await api.post(`/notifications/${id}/read`);
      return id;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(
        error.response?.data?.error || error.response?.data?.message || 'Échec.',
      );
    }
  },
  {
    condition: (id, { getState }) => {
      const notif = getState().NotificationSlice.notifications.find(n => n.id === id);
      return !notif?.lu;            // false → thunk pas exécuté du tout
    },
  },
);

/**
 * markAllAsRead — même principe : skip si aucune non-lue.
 */
export const markAllAsRead = createAsyncThunk<
  void,
  void,
  { state: RootState; rejectValue: string }
>(
  'notification/markAllAsRead',
  async (_, thunkAPI) => {
    try {
      await api.post('/notifications/read-all');
    } catch (error: any) {
      return thunkAPI.rejectWithValue(
        error.response?.data?.error || error.response?.data?.message || 'Échec.',
      );
    }
  },
  {
    condition: (_, { getState }) => getState().NotificationSlice.unreadCount > 0,
  },
);

/** Diffusion par un admin du tenant — protégé par permission `notifications.send` côté serveur. */
export const sendBroadcast = createAsyncThunk<
  void,
  SendBroadcastPayload,
  { rejectValue: string }
>('notification/sendBroadcast', async (payload, thunkAPI) => {
  try {
    await api.post('/notifications/send', payload);
  } catch (error: any) {
    return thunkAPI.rejectWithValue(
      error.response?.data?.error || error.response?.data?.message || 'Échec de la diffusion.',
    );
  }
});

// ── Slice ─────────────────────────────────────────────────────

const notificationSlice = createSlice({
  name: 'notification',
  initialState,
  reducers: {
    /** Appelé par le hook WS quand NOTIFICATION_NEW est reçu — prépend au panneau et incrémente le badge */
    addNotificationFromWs: (state, action: PayloadAction<Notification>) => {
      // Évite les doublons si l'event arrive deux fois (race fetch/WS)
      if (state.notifications.find(n => n.id === action.payload.id)) return;
      state.notifications.unshift(action.payload);
      state.total       += 1;
      state.unreadCount += 1;
    },
    /** Ajoute un toast persistant — appelé par useNotificationListener si popup=true */
    pushToast: (state, action: PayloadAction<ActiveToast>) => {
      // Pas de doublon si le même notif arrive plusieurs fois
      if (state.activeToasts.find(t => t.notification_id === action.payload.notification_id)) return;
      state.activeToasts.push(action.payload);
      // FIFO : on retire le plus ancien si on dépasse la limite
      if (state.activeToasts.length > TOAST_STACK_MAX) state.activeToasts.shift();
    },
    /** Ferme un toast (clic ✕ user, ou auto-fermeture après markAsRead) */
    dismissToast: (state, action: PayloadAction<number>) => {
      state.activeToasts = state.activeToasts.filter(t => t.notification_id !== action.payload);
    },
    clearNotifications: (state) => {
      state.notifications = [];
      state.total         = 0;
      state.totalPages    = 0;
      state.currentPage   = 0;
    },
    clearMessages: (state) => {
      state.error          = null;
      state.successMessage = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchNotifications.pending, (state) => {
        state.loading = true;
        state.error   = null;
      })
      .addCase(fetchNotifications.fulfilled, (state, action) => {
        state.loading       = false;
        state.notifications = action.payload.data;
        state.total         = action.payload.total;
        state.totalPages    = action.payload.totalPages;
        state.currentPage   = action.payload.currentPage;
      })
      .addCase(fetchNotifications.rejected, (state, action) => {
        state.loading = false;
        state.error   = action.payload as string;
      })

      .addCase(fetchUnreadCount.fulfilled, (state, action) => {
        state.unreadCount = action.payload;
      })

      // markAsRead — optimistic UI : update state IMMÉDIATEMENT (avant API)
      .addCase(markAsRead.pending, (state, action) => {
        const id = action.meta.arg;
        const notif = state.notifications.find(n => n.id === id);
        if (notif && !notif.lu) {
          notif.lu          = true;
          notif.lu_at       = new Date().toISOString();
          state.unreadCount = Math.max(0, state.unreadCount - 1);
        }
        // Auto-fermeture du toast correspondant — UX : si le user marque lu depuis
        // le drawer cloche, le toast à l'écran disparaît sans qu'il ait à cliquer ✕
        state.activeToasts = state.activeToasts.filter(t => t.notification_id !== id);
      })
      .addCase(markAsRead.fulfilled, () => {
        // Rien à faire — déjà appliqué dans .pending
      })
      .addCase(markAsRead.rejected, (state, action) => {
        // Rollback : remettre lu=false et le compteur (le toast n'est PAS restauré
        // — UX : on ne re-affiche pas un popup déjà fermé visuellement)
        const id = action.meta.arg;
        const notif = state.notifications.find(n => n.id === id);
        if (notif?.lu) {
          notif.lu          = false;
          notif.lu_at       = null;
          state.unreadCount += 1;
        }
        state.error = action.payload as string ?? 'Échec du marquage';
      })

      // markAllAsRead — optimistic UI similaire
      .addCase(markAllAsRead.pending, (state) => {
        const now = new Date().toISOString();
        state.notifications.forEach(n => {
          if (!n.lu) { n.lu = true; n.lu_at = now; }
        });
        state.unreadCount  = 0;
        state.activeToasts = [];
      })
      .addCase(markAllAsRead.fulfilled, () => {
        // Rien à faire — déjà appliqué dans .pending
      })
      .addCase(markAllAsRead.rejected, (state, action) => {
        // Rollback partiel : on resync depuis le serveur via fetchUnreadCount
        // (pas de rollback per-notif car on ne sait pas lesquelles étaient unread avant).
        // Le state local reste optimiste mais le compteur sera re-fiable au prochain
        // fetchUnreadCount (déclenché à la prochaine notif WS).
        state.error = action.payload as string ?? 'Échec du marquage groupé';
      })

      .addCase(sendBroadcast.pending, (state) => {
        state.sending        = true;
        state.error          = null;
        state.successMessage = null;
      })
      .addCase(sendBroadcast.fulfilled, (state) => {
        state.sending        = false;
        state.successMessage = 'Notification diffusée avec succès.';
      })
      .addCase(sendBroadcast.rejected, (state, action) => {
        state.sending = false;
        state.error   = action.payload as string;
      });
  },
});

export const {
  addNotificationFromWs,
  pushToast,
  dismissToast,
  clearNotifications,
  clearMessages,
} = notificationSlice.actions;

export default notificationSlice.reducer;
